import React, {
  useEffect,
  useState,
  useCallback,
  forwardRef,
  memo,
} from "react";
import { Button, Col, Row } from "react-bootstrap";
import { SuperTrackMarkupSuperTrack } from "@/pages/SuperTracks/SuperTrackMarkupSuperTrack";
import { SuperTrackMarkupControls } from "@/pages/SuperTracks/SuperTrackMarkupControls";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ScrollSync, ScrollSyncPane } from "react-scroll-sync";
import {
  addSuperTrack,
  setSuperTracks,
  setSelectedTracks,
  setSelectedSuperTrack,
  setSelectedSuperTracks,
  updateSelectedTracks,
} from "@/store/supertrackSlice";
import { matchApi } from "../../api";

import "../SuperTracks/SuperTrackMarkup.scss";
import { cloneDeep } from "lodash";

const MemoizedSuperTrackControls = React.memo(SuperTrackMarkupControls);
const MemoizedSuperTrack = React.memo(SuperTrackMarkupSuperTrack);

const SuperTrackMarkupPanel = forwardRef(function SuperTrackMarkupPanel(
  { mainSuperTrackId, visibleSuperTrackIds, showAll },
  posRef
) {
  const { videoId } = useParams();
  const dispatch = useDispatch();
  const { superTracks, selectedSuperTracks } = useSelector(
    ({ superTracks }) => superTracks
  );
  const { user } = useSelector(({ auth }) => auth); // Add this line to get user info

  const [localSuperTracks, setLocalSuperTracks] = useState([]);
  const [allSuperTracks, setAllSuperTracks] = useState([]);

  useEffect(() => {
    const visibleSuperTracks = localSuperTracks.filter(
      ({ isHidden }) => !isHidden
    );
    const hiddenSuperTracks = localSuperTracks.filter(
      ({ isHidden }) => isHidden
    );
    setAllSuperTracks([...visibleSuperTracks, ...hiddenSuperTracks]);
  }, [localSuperTracks]);

  useEffect(() => {
    if (!superTracks) return;

    let tracks = [];
    tracks.push({
      ...superTracks[mainSuperTrackId],
      id: mainSuperTrackId,
    });
    Object.entries(superTracks).forEach(([id, data]) => {
      if (
        visibleSuperTrackIds.includes(id) &&
        Number(id) !== Number(mainSuperTrackId)
      )
        tracks.push({
          ...data,
          id,
        });
    });
    setLocalSuperTracks(tracks);
  }, [superTracks, visibleSuperTrackIds]);

  // TODO: we should split hide and select logic but now it's in one place (selectedTracks)
  const onSuperTrackHide = useCallback(
    (superTrackId) => {
      const clone = [...localSuperTracks];
      const index = clone.findIndex(({ id }) => id === superTrackId);

      clone[index].isHidden = !clone[index].isHidden;

      const newSuperTracks = {
        ...superTracks,
        [superTrackId]: {
          ...superTracks[superTrackId],
          isHidden: !superTracks[superTrackId].isHidden,
        },
      };
      dispatch(setSuperTracks(newSuperTracks));

      // Only update selectedTracks if it's the selected super track.
      // This flow is not a good choice :(
      if (selectedSuperTracks[superTrackId]) {
        dispatch(
          updateSelectedTracks({
            isAdding: !newSuperTracks[superTrackId].isHidden,
            ids: superTracks[superTrackId].tracks.map(
              ({ track_id }) => track_id
            ),
          })
        );
      }
    },
    [localSuperTracks, superTracks]
  );

  const selectAll = () => {
    const clonedSelectedSuperTracks = cloneDeep(selectedSuperTracks);
    let selectedTrackIds = [];
    for (let id in superTracks) {
      clonedSelectedSuperTracks[id] = true;
      selectedTrackIds = [
        ...selectedTrackIds,
        ...superTracks[id].tracks.map(({ track_id }) => track_id),
      ];
    }
    dispatch(setSelectedSuperTracks({ ...clonedSelectedSuperTracks }));
    dispatch(setSelectedTracks(selectedTrackIds));
  };

  const deselectAll = () => {
    const clonedSelectedSuperTracks = cloneDeep(selectedSuperTracks);
    for (let id in selectedSuperTracks) {
      clonedSelectedSuperTracks[id] = false;
    }
    dispatch(setSelectedTracks([]));
    dispatch(setSelectedSuperTrack(null));
    dispatch(setSelectedSuperTracks({ ...clonedSelectedSuperTracks }));
  };

  const createSuperTrack = async () => {
    try {
      const superTrack = await matchApi.createSuperTrack(videoId);
      dispatch(addSuperTrack(superTrack));
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      {showAll ? (
        <Row className="justify-content-center mt-0">
          <Col xs={11} className="p-0">
            <Button
              variant="success"
              size="sm"
              className="mx-1 my-1"
              onClick={selectAll}
            >
              Select All
            </Button>
            <Button
              variant="success"
              size="sm"
              className="mx-1 my-1"
              onClick={deselectAll}
            >
              Deselect All
            </Button>
            <Button
              variant="success"
              size="sm"
              className="mx-1 my-1"
              onClick={createSuperTrack}
            >
              Add Super Track
            </Button>
          </Col>
        </Row>
      ) : null}
      <Row className="justify-content-center mt-1">
        <Col xs={11} className="super-track__container p-0">
          <ScrollSync>
            <div className="super-track__panel">
              <div className="super-track__track-controls-outer">
                <ScrollSyncPane>
                  <div className="super-track__track-controls">
                    {allSuperTracks.map((superTrack) => (
                      <MemoizedSuperTrackControls
                        key={`${superTrack.id}- ${
                          superTrack.isHidden ? "hidden" : "visible"
                        }`}
                        superTrack={superTrack}
                        onHide={onSuperTrackHide}
                        showExtra={false}
                        isManager={user?.is_staff} // Add this prop
                      />
                    ))}
                  </div>
                </ScrollSyncPane>
              </div>
              <div className="super-track__panel-track-container">
                <div className="super-track__indicator" ref={posRef} />
                <ScrollSyncPane>
                  <div className="inner-container">
                    {allSuperTracks.map((superTrack) => (
                      <MemoizedSuperTrack
                        key={`${superTrack.id}- ${
                          superTrack.isHidden ? "hidden" : "visible"
                        }`}
                        superTrack={superTrack}
                      />
                    ))}
                  </div>
                </ScrollSyncPane>
              </div>
            </div>
          </ScrollSync>
        </Col>
      </Row>
    </>
  );
});

export default memo(SuperTrackMarkupPanel);
